export type Channel = 'email' | 'googlePost' | 'socialPost' | 'text';

export type CreationMode = 'createOwn' | 'helpMe';

export type BuilderStep = 'builder' | 'channelSelection' | 'completed' | 'modeSelection' | null;

export type BuilderContext = {
  activeChannel?: Channel;
  campaignId?: number | null;
  creationMode?: CreationMode;
  emailChannelId?: number | null;
  followerSegmentId?: number | null;
  googlePostChannelId?: number | null;
  isIndividualFollowerSelection?: boolean;
  remarketedAction?: string | null;
  remarketedCampaignItemId?: number | null;
  remarketedLocationId?: number | null;
  remarketedTagId?: number | null;
  restaurantId?: number;
  socialChannelId?: number | null;
  textChannelId?: number | null;
};

export type BuilderState = {
  channels: Channel[];
  context: BuilderContext;
  showEmailTemplateModal: boolean;
  showSaveAsDraftConfirmation: boolean;
  step: BuilderStep | null;
};

export type BuilderEvent =
  | { type: 'campaignBuilder.closeEmailTemplateModal' }
  | { type: 'campaignBuilder.closeSaveAsDraftConfirmation' }
  | { type: 'campaignBuilder.exit' }
  | { type: 'campaignBuilder.goToBuilder'; creationMode: CreationMode }
  | { type: 'campaignBuilder.openEmailTemplateModal' }
  | { type: 'campaignBuilder.openSaveAsDraftConfirmation' }
  | {
    type: 'campaignBuilder.openUnifiedBuilder';
    activeChannel: Channel;
    creationMode?: CreationMode;
    restaurantId: number;
    campaignId?: number | null;
    textChannelId?: number | null;
    emailChannelId?: number | null;
    socialChannelId?: number | null;
    googlePostChannelId?: number | null;
  }
  | {
    type: 'campaignBuilder.openChannelSelection';
    restaurantId: number;
    followerSegmentId?: number | null;
    isIndividualFollowerSelection?: boolean;
    remarketedAction?: string | null;
    remarketedCampaignItemId?: number | null;
    remarketedLocationId?: number | null;
    remarketedTagId?: number | null;
  }
  | { type: 'campaignBuilder.openModeSelection'; activeChannel?: Channel; channels: Channel[] }
  | { type: 'campaignBuilder.openSuccessModal' }
  | { type: 'campaignBuilder.setCampaignId'; campaignId?: number }
  | { type: 'campaignBuilder.setEmailChannelId'; emailChannelId: number | null }
  | { type: 'campaignBuilder.setSocialChannelId'; socialChannelId: number | null }
  | { type: 'campaignBuilder.setTextChannelId'; textChannelId: number | null };

export const builderInitialState: BuilderState = {
  channels: [],
  context: {},
  showEmailTemplateModal: false,
  showSaveAsDraftConfirmation: false,
  step: null,
};

export const campaignBuilderReducer = (
  prevState: BuilderState = builderInitialState,
  event: BuilderEvent,
): BuilderState => {
  switch (event.type) {
    case 'campaignBuilder.openEmailTemplateModal':
      return {
        ...prevState,
        showEmailTemplateModal: true,
      };
    case 'campaignBuilder.closeEmailTemplateModal':
      return {
        ...prevState,
        showEmailTemplateModal: false,
      };
    case 'campaignBuilder.openSaveAsDraftConfirmation':
      return {
        ...prevState,
        showSaveAsDraftConfirmation: true,
      };
    case 'campaignBuilder.closeSaveAsDraftConfirmation':
      return {
        ...prevState,
        showSaveAsDraftConfirmation: false,
      };
    case 'campaignBuilder.openChannelSelection':
      return {
        channels: [],
        context: {
          restaurantId: event.restaurantId,
          ...(event.followerSegmentId != null ? { followerSegmentId: event.followerSegmentId } : {}),
          ...(event.isIndividualFollowerSelection ? { isIndividualFollowerSelection: true } : {}),
          ...(event.remarketedCampaignItemId != null ? {
            remarketedAction: event.remarketedAction ?? null,
            remarketedCampaignItemId: event.remarketedCampaignItemId,
            remarketedLocationId: event.remarketedLocationId ?? null,
            remarketedTagId: event.remarketedTagId ?? null,
          } : {}),
        },
        showEmailTemplateModal: false,
        showSaveAsDraftConfirmation: false,
        step: 'channelSelection',
      };
    case 'campaignBuilder.openModeSelection':
      return {
        ...prevState,
        channels: event.channels,
        context: {
          ...prevState.context,
          activeChannel: event.activeChannel ?? event.channels[0],
        },
        step: 'modeSelection',
      };
    case 'campaignBuilder.openSuccessModal':
      return {
        ...prevState,
        step: 'completed',
      };
    case 'campaignBuilder.goToBuilder':
      return {
        ...prevState,
        context: { ...prevState.context, creationMode: event.creationMode },
        step: 'builder',
      };
    case 'campaignBuilder.openUnifiedBuilder':
      return {
        channels: [event.activeChannel],
        context: {
          activeChannel: event.activeChannel,
          creationMode: event.creationMode ?? 'createOwn',
          restaurantId: event.restaurantId,
          ...(event.campaignId !== undefined ? { campaignId: event.campaignId } : {}),
          ...(event.textChannelId !== undefined ? { textChannelId: event.textChannelId } : {}),
          ...(event.emailChannelId !== undefined ? { emailChannelId: event.emailChannelId } : {}),
          ...(event.socialChannelId !== undefined ? { socialChannelId: event.socialChannelId } : {}),
          ...(event.googlePostChannelId !== undefined ? { googlePostChannelId: event.googlePostChannelId } : {}),
        },
        showEmailTemplateModal: false,
        showSaveAsDraftConfirmation: false,
        step: 'builder',
      };
    case 'campaignBuilder.setCampaignId':
      return {
        ...prevState,
        context: { ...prevState.context, campaignId: event.campaignId },
      };
    case 'campaignBuilder.setEmailChannelId':
      return {
        ...prevState,
        context: { ...prevState.context, emailChannelId: event.emailChannelId },
      };
    case 'campaignBuilder.setSocialChannelId':
      return {
        ...prevState,
        context: { ...prevState.context, socialChannelId: event.socialChannelId },
      };
    case 'campaignBuilder.setTextChannelId':
      return {
        ...prevState,
        context: {
          ...prevState.context,
          textChannelId: event.textChannelId,
        },
      };
    case 'campaignBuilder.exit':
      return builderInitialState;
    default:
      return prevState;
  }
};
